/**
 * Leaderboard repository – reads workouts with filters (exercise, date range).
 * Strategy: heaviest weight wins; we query workouts and rank in-memory or via composite query.
 */
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { createWorkout } from '../../domain/entities/Workout';

const COLLECTION = 'workouts';

export function leaderboardRepository({ getFirestore = () => db } = {}) {
  const firestore = getFirestore();
  const coll = () => collection(firestore, COLLECTION);

  /**
   * Fetch top lifts by exercise. If includeUnverified is true, returns both verified and
   * unverified lifts merged and sorted by weight desc (unverified can be shown as "Pending").
   * Otherwise verified-only, sorted by weight desc.
   */
  async function getTopByExercise(exercise, limitCount = 50, startDate = null, endDate = null, includeUnverified = true) {
    const filterByDate = (list) => {
      if (!startDate && !endDate) return list;
      return list.filter((w) => {
        const t = w.createdAt ? new Date(w.createdAt).getTime() : 0;
        if (startDate && t < new Date(startDate).getTime()) return false;
        if (endDate && t > new Date(endDate).getTime()) return false;
        return true;
      });
    };

    if (!includeUnverified) {
      const q = query(
        coll(),
        where('exercise', '==', exercise),
        where('verified', '==', true),
        orderBy('weight', 'desc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      const items = [];
      snap.forEach((d) => items.push(createWorkout({ id: d.id, ...d.data() })));
      return filterByDate(items);
    }

    const [verifiedSnap, unverifiedSnap] = await Promise.all([
      getDocs(query(coll(), where('exercise', '==', exercise), where('verified', '==', true), orderBy('weight', 'desc'), limit(limitCount))),
      getDocs(query(coll(), where('exercise', '==', exercise), where('verified', '==', false), orderBy('weight', 'desc'), limit(limitCount))),
    ]);
    const items = [];
    verifiedSnap.forEach((d) => items.push(createWorkout({ id: d.id, ...d.data() })));
    unverifiedSnap.forEach((d) => items.push(createWorkout({ id: d.id, ...d.data() })));
    const merged = items.sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0)).slice(0, limitCount);
    return filterByDate(merged);
  }

  return {
    getTopByExercise,
  };
}

export const leaderboardRepo = leaderboardRepository();
